<template>
  <label :for="checkboxId" class="pcb-checkbox" :class="{ 'pcb-checkbox--disabled': disabled }" @click="handleClick">
    <div class="pcb-checkbox__checkmark">
      <input 
        :id="checkboxId" 
        type="checkbox" 
        :checked="modelValue"
        :disabled="disabled"
        class="pcb-checkbox__input"
        @change="handleChange"
      >
      <div class="pcb-checkbox__flip" :class="{ 'pcb-checkbox__flip--checked': modelValue }">
        <div class="pcb-checkbox__front"></div>
        <div class="pcb-checkbox__back">
          <svg class="pcb-checkbox__icon" viewBox="0 0 16 14" height="14" width="16">
            <path d="M2 8.5L6 12.5L14 1.5"></path>
          </svg>
        </div>
      </div>
    </div>
  </label>
</template>
<script setup>
import { computed } from 'vue'

defineOptions({
  name: 'checkbox-item'
})

const props = defineProps({
  // 禁用状态
  disabled: {
    type: Boolean,
    default: false
  },
  // 组件ID，用于label关联
  id: {
    type: String,
    default: undefined
  },
  // 主题颜色
  color: {
    type: String,
    default: '#0060FF'
  }
})
const color=computed(()=>{
  return props.color
})
const emit = defineEmits(['change'])

const modelValue = defineModel({
  default: false,
})



// 生成唯一ID
const checkboxId = computed(() => {
  return props.id || `pcb-checkbox-${Math.random().toString(36).substr(2, 9)}`
})

// 计算实际使用的颜色
const actualColor = computed(() => {
  // 如果是预设颜色名称，返回对应的颜色值
  if (colorPresets[props.color.toLowerCase()]) {
    return colorPresets[props.color.toLowerCase()]
  }
  // 否则直接使用传入的颜色值
  return props.color
})

// 计算边框颜色和背景颜色
const colorStyles = computed(() => {
  const color = actualColor.value
  return {
    borderColor: color,
    backgroundColor: color,
    boxShadow: `0 0 0 1px ${color}`
  }
})

// 处理点击事件
const handleClick = (event) => {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  // 手动切换 checkbox 状态
  // 由于 input 的 pointer-events 是 none，需要手动处理
  event.preventDefault() // 阻止浏览器默认行为，避免触发 input 的 change
  const newValue = !modelValue.value
  modelValue.value = newValue
  emit('change', newValue)
}

// 处理输入改变事件
const handleChange = (event) => {
  if (props.disabled) return
  const checked = event.target.checked
  modelValue.value = checked
  emit('change', checked)
}
</script>
<style scoped>
/* 主容器 */
.pcb-checkbox {
  -webkit-perspective: 20;
  line-height: 20px;
  perspective: 20;
  display: inline-block;
  position: relative;
  border: 2px solid #e8e8eb;
  background: #e8e8eb;
  border-radius: 4px;
  transform: translate3d(0, 0, 0);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 20px;
  height: 20px;
}

.pcb-checkbox:hover {
  border-color: v-bind(color);
}

.pcb-checkbox--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 选择框容器 */
.pcb-checkbox__checkmark {
  position: relative;
  width: 100%;
  height: 100%;
}

/* 隐藏原生input */
.pcb-checkbox__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

/* 翻转容器 */
.pcb-checkbox__flip {
  display: block;
  transition: all 0.4s ease;
  transform-style: preserve-3d;
  position: relative;
  width: 20px;
  height: 20px;
  margin: 0;
}

.pcb-checkbox__flip--checked {
  transform: rotateY(180deg);
}

/* 正面和背面 */
.pcb-checkbox__front,
.pcb-checkbox__back {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 2px;
}

/* 正面样式（未选中状态） */
.pcb-checkbox__front {
  background: #fff;
  z-index: 1;
}

/* 背面样式（选中状态） */
.pcb-checkbox__back {
  transform: rotateY(180deg);
  background: v-bind(color);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px v-bind(color);
}

/* 选中图标 */
.pcb-checkbox__icon {
  fill: none;
  width: 14px;
  height: 14px;
}

.pcb-checkbox__icon path {
  stroke: #fff;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* 选中状态下的样式 */
.pcb-checkbox__input:checked + .pcb-checkbox__checkmark .pcb-checkbox {
  border-color: v-bind(color);
}

/* 禁用状态 */
.pcb-checkbox--disabled .pcb-checkbox__flip {
  pointer-events: none;
}

.pcb-checkbox--disabled .pcb-checkbox__back {
  background: #ccc;
  box-shadow: 0 0 0 1px #ccc;
}


</style>